import React from 'react';
import { useSession } from 'next-auth/react';
import useSWR from 'swr';
import { handleErrors } from '../../utils/errors';
import { NotificationsContext } from '../NotificationsProvider';
import NextAnchor from '../NextAnchor';

/**
 * Higher-Order Component for plan-based feature gating
 * @param {React.Component} WrappedComponent - The component to wrap
 * @param {Object} config - Configuration object
 * @param {Array} config.requiredPlans - Array of required plans ['enterprise', 'scale']
 * @param {string} config.featureName - Name of the feature for messaging
 * @param {string} config.upgradeUrl - URL for upgrade link (default: pricing page)
 * @param {string} config.dataSource - Where to get plan data from ('session' | 'api')
 * @param {string} config.apiEndpoint - API endpoint to fetch plan data (when dataSource is 'api')
 * @param {Function} config.customTeaserRenderer - Custom function to render teaser content
 * @param {Function} config.customButtonRenderer - Custom function to render disabled button
 */
const withPlanGating = (WrappedComponent, config = {}) => {
    const {
        requiredPlans = ['enterprise'],
        featureName = 'This functionality',
        upgradeUrl = 'https://streamlinedpayments.com/pricing',
        dataSource = 'session',
        apiEndpoint = '/api/settings/billing-statement/current-plan',
        customTeaserRenderer,
        customButtonRenderer
    } = config;

    return function PlanGatedComponent(props) {
        const { data: session } = useSession();
        const notificationCtx = React.useContext(NotificationsContext);

        const fetcher = (url) =>
            fetch(url).then(handleErrors).catch(notificationCtx?.showNotification);

        // Conditionally fetch data from API if needed
        const {data: apiData} = useSWR(
            dataSource === 'api' ? apiEndpoint : null,
            fetcher,
            {
                revalidateOnFocus: false,
                transform: (data) => ({
                    ...data,
                    has_enterprise_plan: data?.plan_name === 'enterprise',
                    has_scale_plan: ['scale', 'premium', 'partner'].includes(data?.plan_name)
                })
            }
        );

        // Determine plan data source
        const planData = dataSource === 'session' ? session?.company : apiData;

        // Check if user has required plans
        const hasRequiredPlan = React.useMemo(() => {
            if (!planData) return false;
            
            return requiredPlans.some(plan => {
                switch (plan.toLowerCase()) {
                case 'enterprise':
                    return planData.has_enterprise_plan;
                case 'scale':
                    return planData.has_scale_plan;
                default:
                    return false;
                }
            });
        }, [planData]);

        // Default teaser renderer
        const defaultTeaserRenderer = () => {
            const planText = requiredPlans.length > 1 
                ? `${requiredPlans.slice(0, -1).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ')} or ${requiredPlans[requiredPlans.length - 1].charAt(0).toUpperCase() + requiredPlans[requiredPlans.length - 1].slice(1)}`
                : requiredPlans[0].charAt(0).toUpperCase() + requiredPlans[0].slice(1);

            return [
                `${featureName} is available as part of the ${planText} plan.`,
                <NextAnchor key="learn-more" hoverIndicator href={upgradeUrl}>
                    Learn more
                </NextAnchor>
            ];
        };

        // Default button renderer
        const defaultButtonRenderer = (originalButton) => {
            if (!originalButton) return null;
            
            return React.cloneElement(originalButton, {
                label: 'Unavailable',
                disabled: true,
                onClick: undefined,
                ...originalButton.props
            });
        };

        // If user has required plan, render the wrapped component normally
        if (hasRequiredPlan) {
            return <WrappedComponent {...props} />;
        }

        // If user doesn't have required plan, render teaser or modified component
        const teaserContent = customTeaserRenderer ? customTeaserRenderer() : defaultTeaserRenderer();
        
        // Pass teaser content and button renderer to wrapped component
        return (
            <WrappedComponent 
                {...props} 
                planGating={{
                    hasRequiredPlan: false,
                    teaserContent,
                    buttonRenderer: customButtonRenderer || defaultButtonRenderer,
                    requiredPlans,
                    featureName
                }}
            />
        );
    };
};

export default withPlanGating;